import {
  CallEffect,
  CancelEffect,
  ForkEffect,
  TakeEffect,
  call,
  cancel,
  cancelled,
  delay,
  fork,
  put,
  take,
  select,
} from "redux-saga/effects";
import { END, eventChannel, Task } from "redux-saga";
import { finalizeFinchatMessage, markToolCompleted, markToolStarted, updateFinchatOutput, updateMetadata, finchatMessageCancel, finchatMessageCancelAll, finchatMessageLoadFailure, finchatMessageLoad } from "../slices/finchatSlice";
import { PayloadAction } from "@reduxjs/toolkit";
import { FinchatMessagePayload, ServerEvents } from "../../assets/interfaces/interfaces";
import { fetchActivePlan, updatePromptsLeft } from "../slices/activePlanSlice";
import { AllPlanState } from "../../assets/interfaces/interfaces";
import { updateLatestSummaryFromLLM } from "../slices/newsSummarySlice";

function fetchWithTimeout(url: string, options, timeout = 60000) { // Default timeout set to 30 seconds
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeout),
    ),
  ]);
}
export function createEventChannel(stream: ReadableStream) {
  return eventChannel((emitter) => {
    const reader = stream.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";

    function processBuffer() { // extracts event blocks to emit structured objects to the saga
      const lines = buffer.split("\n\n"); // split accumulated buffer into chunks
      for (let i = 0; i < lines.length - 1; i++) {
        const entry = lines[i];
        const eventMatch = entry.match(/^event:\s*(\w+)/m);
        const dataMatch = entry.match(/^data:\s*(.*)/m);

        if (eventMatch && dataMatch) {
          const event = eventMatch[1];
          const data = dataMatch[1];
          let parsedData = data;

          try {
            parsedData = JSON.parse(data);
          }
          catch (_) {
            // fallback to raw string if is not valid JSON
          }

          emitter({ event, data: parsedData });
        }
      }
      buffer = lines[lines.length - 1];
    }

    function read() {
      reader.read().then(({ done, value }) => {
        if (done) {
          emitter(END);
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        processBuffer();
        read();
      });
    }

    read();

    return () => reader.cancel();
  });
}
// Utility function to check the plan status
const getPlanStatus = (state: { plan: AllPlanState }) => state.plan.status;
function* waitForPlanSuccess() {
  let status = yield select(getPlanStatus);
  let attempts = 0; // Counter for attempts
  while (status !== "succeeded" && attempts < 5) {
    if (status === "idle" || status === "failed") {
      yield put(fetchActivePlan());
      attempts++; // Increment the attempt counter
    }
    yield delay(1000); // Check every 1 second
    status = yield select(getPlanStatus);
  }
  if (attempts >= 5) {
    yield put(finchatMessageLoadFailure());
    // For example, log an error, update the state with an error message, etc.
    console.error("Failed to fetch active plan after 5 attempts.");
  }
}

function* fetchFinchatMessage(action: PayloadAction<FinchatMessagePayload>) {
  const user_query = action.payload.input;
  const { user_id, chat_id, session_id, input_type, attachments, preferences } = action.payload;
  yield call(waitForPlanSuccess);
  // active plan endpoint has succeeded
  try {
    const query: Partial<FinchatMessagePayload & { user_query: string }> = {
      session_id,
      user_query,
      chat_id,
      user_id,
      input_type,
      preferences,
    };
    if (attachments) {
      query.attachments = attachments;
    }
    const response = yield call(
      fetchWithTimeout,
      `${process.env.REACT_APP_PYTHON_APi_URL}/v2/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "platform": "web",
        },
        credentials: "include",
        body: JSON.stringify(query),
      });
    if (!response.body) {
      throw new Error("No response body");
    }
    const channel = yield call(createEventChannel, response.body);
    try {
      while (true) {
        const eventObject = yield take(channel);
        const { event, data } = eventObject;
        switch (event) {
          case ServerEvents.LLM_STREAM:
            yield put(updateFinchatOutput({ session_id, output: data }));
            break;

          case ServerEvents.NEWS: {
            // if data is empty array, don't need dispatch
            if (Array.isArray(data.news_summary) && data.news_summary.length > 0) {
              yield put(updateLatestSummaryFromLLM({
                [session_id]: { chat_id, news_summary: data.news_summary },
              }));
            }
            break;
          }

          case ServerEvents.TOOL_LOAD_START:
            yield put(markToolStarted({ session_id, toolInfo: data }));
            break;

          case ServerEvents.TOOL_LOAD_END:
            yield put(markToolCompleted({ session_id, toolInfo: data }));
            break;

          case ServerEvents.METADATA:
            yield put(updateMetadata({ session_id, metadata: data }));
            yield put(updatePromptsLeft(data.prompt_left));
            break;

          case ServerEvents.DONE:
            yield put(finalizeFinchatMessage({ session_id }));
            break;
        }
      }
    }
    finally {
      if (yield cancelled()) {
        if (channel) {
          channel.close();
        }
      }
    }
  }
  catch {
    yield put(finchatMessageLoadFailure());
  }
}

function* watchFinchatMessages(): Generator<TakeEffect | ForkEffect | CallEffect | CancelEffect, void, unknown> {
  let lastTask: Record<string, Task | null> = {};

  while (true) {
    const action = yield take([
      finchatMessageLoad.type,
      finchatMessageCancel.type,
      finchatMessageCancelAll.type,
      finalizeFinchatMessage.type,
    ]);

    if (!action || typeof action !== "object" || !("type" in action)) return;

    if (action.type === finchatMessageLoad.type) {
      const action2 = action as PayloadAction<FinchatMessagePayload>;
      lastTask[action2.payload?.session_id] = (yield fork(fetchFinchatMessage, action2)) as Task | null;
    }

    if (action.type === finchatMessageCancel.type) {
      const action2 = action as PayloadAction<string>;
      if (action2.payload && lastTask[action2.payload]) {
        yield cancel(lastTask[action2.payload]);
        lastTask[action2.payload] = null;
      }
    }

    if (action.type === finchatMessageCancelAll.type) {
      for (const sessionId in lastTask) {
        if (Object.prototype.hasOwnProperty.call(lastTask, sessionId) && lastTask[sessionId]) {
          yield cancel(lastTask[sessionId]);
        }
      }
      lastTask = {};
    }

    if (action.type === finalizeFinchatMessage.type) {
      const action2 = action as PayloadAction<{
        session_id: string;
      }>;
      lastTask[action2.payload.session_id] = null;
    }
  }
}

export default function* finchatSaga() {
  yield fork(watchFinchatMessages);
}
